import { useCallback, useEffect, useState } from 'react';
import { supabase, type Profile, type Subscription, type RefundRequest } from '@/lib/supabase';
import {
  PLANS,
  PLAN_LABELS,
  formatPrice,
  formatExpiry,
  isSubscriptionExpired,
  BANK_INFO,
  TRANSFER_NOTICE,
  APPROVAL_NOTICE,
  REFUND_POLICIES,
  calcRefundAmount,
  calcRemainingMonths,
  type PlanKey,
} from '@/lib/plans';

const planKeys: PlanKey[] = ['1month', '3month', '12month'];

interface Props {
  profile: Profile;
  onChanged?: () => void;
}

export default function SubscriptionPanel({ profile, onChanged }: Props) {
  const [sub, setSub] = useState<Subscription | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('1month');
  const [showRenew, setShowRenew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(
    null,
  );
  const [refundReq, setRefundReq] = useState<RefundRequest | null>(null);
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundMsg, setRefundMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(
    null,
  );

  const expired = profile.status === 'expired' || isSubscriptionExpired(profile.subscription_expires_at);
  const pending = profile.status === 'pending';
  const rejected = profile.status === 'rejected';
  const role = profile.role === 'owner' ? 'owner' : 'member';
  const currentPlan = (sub?.plan || profile.subscription_plan) as PlanKey | null;
  const refundable = currentPlan === '3month' || currentPlan === '12month';
  const extensionPending =
    sub?.status === 'pending' && profile.status !== 'pending' && profile.status !== 'rejected';

  const loadSub = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      setSub((data as Subscription) || null);
    } catch (e) {
      console.error(e);
    }
  }, [profile.id]);

  useEffect(() => {
    loadSub();
  }, [loadSub]);

  const loadRefund = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('refund_requests')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      setRefundReq((data as RefundRequest) || null);
    } catch (e) {
      console.error(e);
    }
  }, [profile.id]);

  useEffect(() => {
    loadRefund();
  }, [loadRefund]);

  const requestRenew = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { error } = await supabase.rpc('request_subscription', { p_plan: selectedPlan });
      if (error) {
        setMessage({ type: 'error', text: error.message || '연장 신청 중 오류가 발생했습니다.' });
        return;
      }
      setMessage({ type: 'success', text: '연장 신청이 완료되었습니다. 입금 확인 후 이용권이 연장됩니다.' });
      setShowRenew(false);
      await loadSub();
      onChanged?.();
    } catch (e) {
      setMessage({ type: 'error', text: '연장 신청 중 오류가 발생했습니다.' });
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const submitRefund = async () => {
    setRefundBusy(true);
    setRefundMsg(null);
    try {
      const { error } = await supabase.rpc('request_refund', {
        p_reason: refundReason.trim() || null,
      });
      if (error) {
        setRefundMsg({ type: 'error', text: error.message || '환불 신청 중 오류가 발생했습니다.' });
        return;
      }
      setRefundMsg({ type: 'success', text: '환불 신청이 접수되었습니다. 운영자가 확인 후 환불해드립니다.' });
      setShowRefund(false);
      setRefundReason('');
      await loadRefund();
      onChanged?.();
    } catch (e) {
      setRefundMsg({ type: 'error', text: '환불 신청 중 오류가 발생했습니다.' });
      console.error(e);
    } finally {
      setRefundBusy(false);
    }
  };

  // 거절 상태
  if (rejected) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100 text-red-600">
            <i className="ri-close-circle-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-foreground-950">가입이 거절되었습니다</h3>
            <p className="text-sm text-foreground-500">운영자에게 문의해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  // 입금 확인 대기 상태
  if (pending) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-time-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-foreground-950">입금 확인 대기 중</h3>
            <p className="text-sm text-foreground-500">아래 계좌로 입금하시면 운영자가 확인 후 이용권을 활성화합니다.</p>
            <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1">
              <i className="ri-time-line"></i>
              {APPROVAL_NOTICE}
            </p>
          </div>
        </div>
        <BankInfo plan={sub?.plan || selectedPlan} price={sub?.price} role={role} />
      </div>
    );
  }

  // 연장 신청 승인 대기 상태
  if (extensionPending) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-time-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-foreground-950">연장 신청 승인 대기 중</h3>
            <p className="text-sm text-foreground-500">
              연장 신청이 접수되었습니다. 입금 확인 후 이용권이 연장됩니다.
            </p>
          </div>
        </div>

        {currentPlan && !expired && (
          <div className="mb-3 rounded-lg bg-background-50 border border-background-200 px-4 py-3 text-sm">
            <span className="text-foreground-500">현재 이용권: </span>
            <span className="font-semibold text-foreground-900">
              {profile.subscription_expires_at
                ? `${calcRemainingMonths(profile.subscription_expires_at)}개월 · ${formatExpiry(profile.subscription_expires_at)}`
                : PLAN_LABELS[currentPlan]}
              까지
            </span>
          </div>
        )}

        <div className="mb-4 rounded-lg bg-primary-50 border border-primary-200 px-4 py-3 text-sm">
          <span className="text-foreground-600">신청 이용권: </span>
          <span className="font-semibold text-primary-700">
            {sub?.plan ? PLAN_LABELS[sub.plan as PlanKey] : '이용권'} ·{' '}
            {formatPrice(sub?.price ?? 0)}원
          </span>
          <span className="ml-1 text-xs text-foreground-500">(승인 대기)</span>
        </div>

        <BankInfo plan={sub?.plan} price={sub?.price} role={role} />
      </div>
    );
  }

  // 만료 상태
  if (expired) {
    return (
      <div className="bg-white rounded-xl border border-background-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-100 text-red-600">
            <i className="ri-alert-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-foreground-950">이용기간이 만료되었습니다</h3>
            <p className="text-sm text-foreground-500">이용권을 연장하시면 다시 이용하실 수 있습니다.</p>
          </div>
        </div>
        <RenewForm
          role={role}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          busy={busy}
          message={message}
          onRequest={requestRenew}
        />
      </div>
    );
  }

  // 이용 중 상태
  return (
    <div className="bg-white rounded-xl border border-background-200 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <i className="ri-vip-crown-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-semibold text-foreground-950">이용 중</h3>
            <p className="text-sm text-foreground-500">
              {profile.subscription_expires_at
                ? `${calcRemainingMonths(profile.subscription_expires_at)}개월`
                : '이용권'} ·{' '}
              {profile.subscription_expires_at ? formatExpiry(profile.subscription_expires_at) : '-'}
              까지
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {refundable && (
            <button
              onClick={() => {
                setShowRefund((v) => !v);
                setRefundMsg(null);
              }}
              className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer"
            >
              환불 신청
            </button>
          )}
          <button
            onClick={() => {
              setShowRenew((v) => !v);
              setMessage(null);
            }}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors cursor-pointer"
          >
            이용권 연장
          </button>
        </div>
      </div>

      {refundReq?.status === 'pending' && (
        <div className="mt-4 rounded-lg bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-foreground-700">
          <i className="ri-time-line mr-1"></i>
          환불 신청이 접수되어 운영자 검토 중입니다.
        </div>
      )}
      {refundReq?.status === 'approved' && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          <i className="ri-check-line mr-1"></i>
          환불이 승인되었습니다. {formatPrice(refundReq.refund_amount)}원이 개인 송금으로 환불됩니다.
        </div>
      )}

      {showRefund && refundable && currentPlan && (
        <div className="mt-4 pt-4 border-t border-background-100">
          <RefundForm
            role={role}
            plan={currentPlan}
            refundAmount={calcRefundAmount(role, currentPlan)}
            reason={refundReason}
            setReason={setRefundReason}
            busy={refundBusy}
            message={refundMsg}
            onSubmit={submitRefund}
          />
        </div>
      )}

      {showRenew && (
        <div className="mt-4 pt-4 border-t border-background-100">
          <RenewForm
            role={role}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            busy={busy}
            message={message}
            onRequest={requestRenew}
          />
        </div>
      )}
    </div>
  );
}

function BankInfo({ plan, price, role }: { plan?: string; price?: number; role: 'member' | 'owner' }) {
  const p = (plan as PlanKey) || '1month';
  const amount = price ?? PLANS[role][p];
  return (
    <div className="rounded-lg bg-accent-50 border border-accent-200 px-4 py-3">
      <div className="flex items-center gap-1.5 font-semibold text-foreground-900 text-sm">
        <i className="ri-bank-card-line"></i>
        입금 계좌
      </div>
      <div className="mt-1 text-sm">
        <span className="font-semibold text-emerald-700">
          {BANK_INFO.bank} {BANK_INFO.account}
        </span>
        <span className="text-foreground-600"> · {BANK_INFO.holder}</span>
      </div>
      <div className="text-xs text-foreground-500 mt-1">
        신청 이용권: {PLAN_LABELS[p]} · {formatPrice(amount)}원
      </div>
      <div className="mt-2 pt-2 border-t border-accent-200 text-foreground-700 flex items-start gap-1.5">
        <i className="ri-alert-line mt-0.5 text-accent-700 flex-shrink-0"></i>
        <span>{TRANSFER_NOTICE}</span>
      </div>
    </div>
  );
}

function RenewForm({
  role,
  selectedPlan,
  setSelectedPlan,
  busy,
  message,
  onRequest,
}: {
  role: 'member' | 'owner';
  selectedPlan: PlanKey;
  setSelectedPlan: (p: PlanKey) => void;
  busy: boolean;
  message: { type: 'error' | 'success'; text: string } | null;
  onRequest: () => void;
}) {
  return (
    <div className="space-y-3">
      <span className="block text-sm font-medium text-foreground-700">이용권 선택</span>
      <div className="grid grid-cols-3 gap-2">
        {planKeys.map((key) => {
          const selected = selectedPlan === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedPlan(key)}
              className={`whitespace-nowrap rounded-lg border p-3 text-center transition-colors cursor-pointer ${
                selected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-background-200 bg-white hover:border-background-300'
              }`}
            >
              <div className="text-sm font-semibold text-foreground-950">{PLAN_LABELS[key]}</div>
              <div className="text-xs text-primary-700 font-semibold mt-1">
                {formatPrice(PLANS[role][key])}원
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5 text-xs text-foreground-700">
        <div className="font-semibold text-foreground-900">
          {BANK_INFO.bank} {BANK_INFO.account} · {BANK_INFO.holder}
        </div>
        <div className="mt-0.5">입금 후 운영자가 확인하면 이용권이 활성화됩니다.</div>
        <div className="mt-2 pt-2 border-t border-accent-200 flex items-start gap-1.5">
          <i className="ri-alert-line mt-0.5 text-accent-700 flex-shrink-0"></i>
          <span>{TRANSFER_NOTICE}</span>
        </div>
      </div>

      <div className="rounded-md bg-background-100 border border-background-200 px-3 py-2.5 text-xs text-foreground-600">
        <div className="font-semibold text-foreground-800 mb-1.5">환불정책 안내</div>
        <ul className="space-y-1">
          {REFUND_POLICIES.map((policy) => (
            <li key={policy} className="flex items-start gap-1.5">
              <i className="ri-checkbox-circle-line mt-0.5 text-foreground-400 flex-shrink-0"></i>
              <span>{policy}</span>
            </li>
          ))}
        </ul>
      </div>

      {message && (
        <div
          className={`text-sm rounded-md px-3 py-2 ${
            message.type === 'error'
              ? 'text-red-600 bg-red-50 border border-red-200'
              : 'text-emerald-700 bg-emerald-50 border border-emerald-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <button
        type="button"
        onClick={onRequest}
        disabled={busy}
        className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-md transition-colors cursor-pointer disabled:opacity-60"
      >
        {busy ? '신청 중...' : '연장 신청하기'}
      </button>
    </div>
  );
}

function RefundForm({
  role,
  plan,
  refundAmount,
  reason,
  setReason,
  busy,
  message,
  onSubmit,
}: {
  role: 'member' | 'owner';
  plan: PlanKey;
  refundAmount: number;
  reason: string;
  setReason: (v: string) => void;
  busy: boolean;
  message: { type: 'error' | 'success'; text: string } | null;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5 text-xs text-foreground-700">
        <div className="font-semibold text-foreground-900">환불 안내</div>
        <div className="mt-1 leading-relaxed">
          {PLAN_LABELS[plan]} 이용권 환불 시 1개월 이용금액(
          {formatPrice(PLANS[role]['1month'])}원)을 공제하고,{' '}
          <span className="font-semibold text-primary-700">{formatPrice(refundAmount)}원</span>이
          환불됩니다.
        </div>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-foreground-700 mb-1.5">
          환불 사유 (선택)
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
          placeholder="환불 사유를 입력해주세요."
        />
      </label>

      {message && (
        <div
          className={`text-sm rounded-md px-3 py-2 ${
            message.type === 'error'
              ? 'text-red-600 bg-red-50 border border-red-200'
              : 'text-emerald-700 bg-emerald-50 border border-emerald-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={busy}
        className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-md transition-colors cursor-pointer disabled:opacity-60"
      >
        {busy ? '신청 중...' : '환불 신청하기'}
      </button>
    </div>
  );
}